import React, { useEffect, useState, useMemo } from "react";
import { X, Sparkles, ArrowRight, Check, Anchor, MinusCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import clsx from "clsx";

import { dayRolloverApi } from "@/lib/api";
import { roomColor } from "./FilterBar";
import { fmtTime } from "@/lib/format";

const ACTION_OPTS = [
  { key: "rollover", label: "Roll forward", short: "→ TODAY", icon: ArrowRight, hint: "Move the block to today, same time, drift reset to 0" },
  { key: "done", label: "Mark done", short: "DONE", icon: Check, hint: "Task status → done, block stays where it is" },
  { key: "floating", label: "Keep floating", short: "FLOAT", icon: Anchor, hint: "Delete the block; task stays unscheduled in its room" },
  { key: "skip", label: "Skip", short: "SKIP", icon: MinusCircle, hint: "No change — handle this one manually" },
];

const PRI_COLORS = {
  critical: "border-red text-red",
  high: "border-amber text-amber",
  normal: "border-line text-ink",
  low: "border-line text-ink-mute",
};

/**
 * AI-light day-rollover dialog.
 *
 * Triggers automatically on the first load of a new day per project. Lists
 * tasks whose block ended before midnight today and whose status is not done.
 * The producer picks a bulk action per task (or "Apply all → roll forward")
 * and we send the decisions to the backend in one shot.
 *
 * Mute state persisted under `ps.rollover.muted.<projectId>.<date>` so the
 * dialog won't pop again today after a dismiss.
 */
export default function DayRolloverDialog({ open, onClose, projectId, projectTimezone = "UTC", onApplied }) {
  const today = useMemo(() => deriveToday(projectTimezone), [projectTimezone]);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [decisions, setDecisions] = useState({}); // {task_id: action}

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    dayRolloverApi.candidates(projectId, today)
      .then((r) => {
        setCandidates(r.candidates || []);
        // Smart defaults: critical/high → rollover, others → skip.
        const seed = {};
        for (const c of (r.candidates || [])) {
          seed[c.task_id] = (c.priority === "critical" || c.priority === "high") ? "rollover" : "skip";
        }
        setDecisions(seed);
      })
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load yesterday's tasks"))
      .finally(() => setLoading(false));
  }, [open, projectId, today]);

  if (!open) return null;

  const counts = candidates.reduce((acc, c) => {
    const k = decisions[c.task_id] || "skip";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});

  const setAll = (action) => {
    const next = {};
    for (const c of candidates) next[c.task_id] = action;
    setDecisions(next);
  };

  const dismiss = (muteToday) => {
    if (muteToday) {
      try {
        localStorage.setItem(muteKey(projectId, today), "1");
      } catch (e) {
        console.warn("rollover mute persist failed", e);
      }
    }
    onClose();
  };

  const apply = async () => {
    setApplying(true);
    try {
      const actions = candidates.map((c) => ({
        task_id: c.task_id,
        block_id: c.block_id,
        action: decisions[c.task_id] || "skip",
      }));
      const res = await dayRolloverApi.apply(projectId, today, actions);
      const bits = [];
      if (res.rolled) bits.push(`${res.rolled} rolled forward`);
      if (res.done) bits.push(`${res.done} marked done`);
      if (res.floated) bits.push(`${res.floated} kept floating`);
      toast.success(bits.join(" · ") || "Nothing changed");
      try { localStorage.setItem(muteKey(projectId, today), "1"); } catch (e) { console.warn("rollover mute persist failed", e); }
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Rollover failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 backdrop-blur-sm"
      onClick={() => dismiss(false)}
      data-testid="rollover-overlay"
    >
      <div
        className="bg-bg-surface border border-line-strong shadow-[0_0_64px_rgba(0,0,0,0.7)] w-[760px] max-w-[95vw] max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="rollover-dialog"
      >
        {/* Header */}
        <header className="flex items-center justify-between px-4 py-3 border-b border-line-strong bg-bg-elevated">
          <div className="flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-amber" />
            <div>
              <div className="mono text-2xs uppercase tracking-[0.2em] text-amber">AI · NEW DAY</div>
              <div className="text-sm text-ink mt-0.5">
                {loading ? "Scanning yesterday…" : (
                  candidates.length === 0
                    ? "All caught up — nothing to roll forward"
                    : `${candidates.length} task${candidates.length === 1 ? "" : "s"} didn't finish — decide what happens next`
                )}
              </div>
            </div>
          </div>
          <button onClick={() => dismiss(false)} className="btn-ghost" data-testid="rollover-close">
            <X className="w-3 h-3" />
          </button>
        </header>

        {/* Bulk-action toolbar */}
        {candidates.length > 0 && (
          <div className="px-4 py-2 border-b border-line bg-bg-base flex items-center gap-2 flex-wrap">
            <span className="mono text-2xs uppercase tracking-widest text-ink-mute mr-2">APPLY TO ALL ·</span>
            {ACTION_OPTS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setAll(opt.key)}
                className="btn-ghost"
                data-testid={`rollover-bulk-${opt.key}`}
                title={opt.hint}
              >
                <opt.icon className="w-3 h-3" />
                {opt.short}
              </button>
            ))}
          </div>
        )}

        {/* Candidates list */}
        <div className="flex-1 overflow-auto p-3 space-y-1.5">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-ink-mute mono text-2xs uppercase tracking-widest">
              <Loader2 className="w-3 h-3 animate-spin mr-2" /> loading…
            </div>
          ) : candidates.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-6 h-6 text-amber mx-auto mb-2 opacity-50" />
              <div className="mono text-2xs uppercase tracking-widest text-ink-mute">
                no incomplete tasks from previous days
              </div>
              <div className="text-2xs text-ink-mute mt-2">All clear. Carry on.</div>
            </div>
          ) : (
            candidates.map((c) => (
              <CandidateRow
                key={c.task_id}
                cand={c}
                action={decisions[c.task_id] || "skip"}
                onAction={(a) => setDecisions((d) => ({ ...d, [c.task_id]: a }))}
              />
            ))
          )}
        </div>

        {/* Footer */}
        <footer className="px-4 py-3 border-t border-line-strong bg-bg-elevated flex items-center gap-3">
          {candidates.length > 0 && (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute flex items-center gap-2 flex-wrap">
              <span>SUMMARY ·</span>
              {counts.rollover > 0 && <span className="text-cyan">{counts.rollover} ROLL</span>}
              {counts.done > 0 && <span className="text-status-done">{counts.done} DONE</span>}
              {counts.floating > 0 && <span className="text-amber">{counts.floating} FLOAT</span>}
              {counts.skip > 0 && <span className="text-ink-mute">{counts.skip} SKIP</span>}
            </div>
          )}
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => dismiss(true)}
              className="btn-ghost"
              disabled={applying}
              data-testid="rollover-mute-today"
              title="Hide this dialog until tomorrow"
            >
              MUTE FOR TODAY
            </button>
            {candidates.length > 0 ? (
              <button
                onClick={apply}
                disabled={applying}
                className="btn-amber"
                data-testid="rollover-apply"
              >
                {applying ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                {applying ? "APPLYING…" : "APPLY"}
              </button>
            ) : (
              <button onClick={() => dismiss(true)} className="btn-amber" data-testid="rollover-acknowledge">
                <Check className="w-3 h-3" /> GOT IT
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}

function CandidateRow({ cand, action, onAction }) {
  const stripe = roomColor(cand.room_color);
  return (
    <div
      className="flex items-center gap-3 px-3 py-2 border border-line hover:border-line-strong bg-bg-base"
      data-testid={`rollover-row-${cand.task_id}`}
    >
      <span className="w-1 self-stretch shrink-0" style={{ background: stripe }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs">
          <span className={clsx("mono text-2xs uppercase tracking-widest border px-1.5 py-0.5", PRI_COLORS[cand.priority] || PRI_COLORS.normal)}>
            {cand.priority}
          </span>
          <span className="text-ink truncate" title={cand.title}>{cand.title}</span>
        </div>
        <div className="mono text-2xs uppercase tracking-widest text-ink-mute mt-0.5">
          {cand.room_abbr} · {cand.start_at.slice(0, 10)} {fmtTime(cand.start_at)} → {fmtTime(cand.end_at)} · {cand.status}
        </div>
      </div>
      <div className="flex items-center gap-0 shrink-0" data-testid={`rollover-row-${cand.task_id}-actions`}>
        {ACTION_OPTS.map((opt, i) => {
          const active = action === opt.key;
          return (
            <button
              key={opt.key}
              onClick={() => onAction(opt.key)}
              className={clsx(
                "mono text-2xs uppercase tracking-widest px-2 py-1 border transition-colors flex items-center gap-1",
                i > 0 && "-ml-px",
                active
                  ? "border-amber text-amber bg-amber-glow z-10"
                  : "border-line text-ink-dim hover:border-line-strong hover:text-ink"
              )}
              data-testid={`rollover-action-${cand.task_id}-${opt.key}`}
              title={opt.hint}
            >
              <opt.icon className="w-3 h-3" />
              {opt.short}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// localStorage key per project per day.
export function muteKey(projectId, today) {
  return `ps.rollover.muted.${projectId}.${today}`;
}

// "Today" in the project's wall-clock timezone. Falls back to local on bad TZ.
export function deriveToday(tz) {
  try {
    const fmt = new Intl.DateTimeFormat("en-CA", { timeZone: tz || "UTC", year: "numeric", month: "2-digit", day: "2-digit" });
    return fmt.format(new Date()); // en-CA gives "YYYY-MM-DD" natively
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}
